import { localizedStrings } from '@core/strings'
import { Entity } from '@core/entities/entity'
import { ProjectLocationStatus } from './types'
import { ProjectPlanStatus } from '../ProjectPlan'
import { Type, plainToInstance } from 'class-transformer'
import { IProjectLocationNode } from './IProjectLocationNode'
import { SiteBoxDocumentPdfVersion } from '@core/entities/SiteBoxDocumentPdfVersion'
import { ProjectLocationNodeBoundary } from './ProjectLocationNodeBoundary'
import { statuses } from '@core/theme/statuses'

export class ProjectLocationNode extends Entity<IProjectLocationNode> {
  id: string

  name: string

  status: ProjectLocationStatus

  planId?: number

  planImageKey?: string

  planStatus: ProjectPlanStatus

  siteBoxBaseDocument?: {
    id: number
    name: string
  }

  @Type(() => SiteBoxDocumentPdfVersion)
  siteBoxDocumentVersion?: SiteBoxDocumentPdfVersion

  @Type(() => ProjectLocationNode)
  locations: ProjectLocationNode[]

  @Type(() => ProjectLocationNodeBoundary)
  boundary?: ProjectLocationNodeBoundary | null

  public static new(payload: unknown): ProjectLocationNode {
    const entity = plainToInstance(ProjectLocationNode, payload)

    return entity
  }

  public isActive(): boolean {
    return this.status === 'ACTIVE'
  }

  public hasSubLocations(): boolean {
    return this.locations.length > 0
  }

  public isArchived(): boolean {
    return this.status === 'ARCHIVED'
  }

  public hasBoundary(): boolean {
    return !!this.boundary?.getGeometry()
  }

  /** True when this node or any of its descendants has a boundary. */
  public hasBoundaryInSubtree(): boolean {
    if (this.hasBoundary()) {
      return true
    }
    return !!this.locations?.some((child) => child.hasBoundaryInSubtree())
  }

  public isPlanEditable(): boolean {
    return this.planStatus === 'UNPUBLISHED' && this.status === 'ACTIVE'
  }

  public getStatusChipColor() {
    switch (this.planStatus) {
      case 'ARCHIVED':
        return statuses.archived.color
      case 'UNPUBLISHED':
        return statuses.unpublished.color
      default:
        return 'success'
    }
  }

  public getStatusString(): string {
    switch (this.planStatus) {
      case 'UNPUBLISHED':
        return localizedStrings.unpublishedPlan
      case 'PUBLISHED':
        return statuses.published.label
      case 'ARCHIVED':
        return localizedStrings.archivedPlan
      default:
        return this.planStatus
    }
  }

  /**
   * Resolves the effective boundary for a target location node by walking up
   * the hierarchy tree.
   *
   * The algorithm:
   * 1. Finds the path from the root to the target node (DFS).
   * 2. Walks the path in reverse (target -> root), returning the first
   *    node that has a boundary configured.
   * 3. Returns null if no node in the chain has a boundary.
   *
   * This enables "boundary inheritance" -- a child location without its own
   * boundary will inherit the nearest ancestor's boundary for map centering.
   *
   * @param targetNodeId - The ID of the location node to resolve the boundary for.
   * @param locations    - The top-level location node array (the full tree).
   * @returns The resolved boundary, or null if none found in the chain.
   */
  public static resolveLocationBoundary(
    targetNodeId: string,
    locations: ProjectLocationNode[],
  ): ProjectLocationNodeBoundary | null {
    const path = ProjectLocationNode.findPathToNode(targetNodeId, locations)
    if (!path) return null

    // Walk from target back to root -- first boundary found wins
    for (let i = path.length - 1; i >= 0; i--) {
      if (path[i].hasBoundary()) {
        return path[i].boundary ?? null
      }
    }

    return null
  }

  /**
   * Flattens the location tree depth-first, returning every node that has
   * its own boundary polygon configured.
   */
  public static flattenNodesWithBoundaries(
    nodes: ProjectLocationNode[],
  ): ProjectLocationNode[] {
    const result: ProjectLocationNode[] = []
    const visit = (currentNodes: ProjectLocationNode[]) => {
      for (const node of currentNodes) {
        if (node.hasBoundary()) {
          result.push(node)
        }
        if (node.locations?.length) {
          visit(node.locations)
        }
      }
    }
    visit(nodes)
    return result
  }

  /**
   * Flattens the tree into rows with their depth, sorting siblings
   * alphabetically at each level, for rendering as an indented list.
   */
  public static flattenNodes(
    nodes: ProjectLocationNode[],
  ): Array<{ node: ProjectLocationNode; depth: number }> {
    const result: Array<{ node: ProjectLocationNode; depth: number }> = []
    const visit = (currentNodes: ProjectLocationNode[], depth: number) => {
      // Copy: the arrays belong to the react-query cache
      const sortedNodes = [...currentNodes].sort((a, b) =>
        a.name.localeCompare(b.name),
      )
      for (const node of sortedNodes) {
        result.push({ node, depth })
        if (node.locations?.length) {
          visit(node.locations, depth + 1)
        }
      }
    }
    visit(nodes, 0)
    return result
  }

  /** Finds a node anywhere in the tree by ID. */
  public static findNodeById(
    nodeId: string,
    nodes: ProjectLocationNode[],
  ): ProjectLocationNode | null {
    const path = ProjectLocationNode.findPathToNode(nodeId, nodes)
    return path ? path[path.length - 1] : null
  }

  /**
   * Performs a depth-first search to find the path from the tree root to the
   * node with the given ID. Returns the ordered list of ancestors including
   * the target, or null if not found.
   */
  private static findPathToNode(
    targetId: string,
    nodes: ProjectLocationNode[],
    path: ProjectLocationNode[] = [],
  ): ProjectLocationNode[] | null {
    for (const node of nodes) {
      const currentPath = [...path, node]
      if (node.id === targetId) return currentPath
      if (node.locations?.length) {
        const result = ProjectLocationNode.findPathToNode(
          targetId,
          node.locations,
          currentPath,
        )
        if (result) return result
      }
    }
    return null
  }
}
